// ---------------------------------------------------------------------------
// Letters selection: the chooser calls vowel or consonant nine times.
//
// Three things here are load-bearing rather than decorative:
//
//   1. One thirty-second budget covers all nine picks. It is the round clock,
//      running straight through, so it never restarts and a stalling caller
//      spends a shared budget rather than nine private ones.
//   2. Legality comes from `canPick` in the shared engine, so a button is
//      disabled exactly when the server would refuse the pick, and the one
//      line under the buttons says what the rest of the board has to be. A dead
//      button with no reason reads as a bug.
//   3. Everyone who is not calling watches the same row fill in. A waiting room
//      here would cost them the first seconds of the round they are about to
//      play.
//   4. Vowel and consonant are peers and are drawn as peers. Consonant used to
//      be an outline button beside a filled one, which read as the disabled
//      half of a primary/secondary pair rather than as one of two live calls.
//      Both are filled now, so the only button that looks off is one that is
//      off, and each carries its keyboard key as a badge.
//   5. A press is answered on the frame it happens and the tile lands when it
//      lands. Those are two different clocks and this screen used to run them
//      as one: a tile was held back for a whole TILE_FLIP_MS beat so the next
//      one could not cut its flip off, which meant a caller tapping quickly
//      pressed and watched nothing happen for half a second. The button
//      acknowledges itself, the called slot lights up at once, and the row
//      keeps only the small stagger that stops two tiles landing in one frame.
//      See `usePacedReveal` and `usePendingPicks`.
//   6. The disc runs straight down and is never held. The budget behind it used
//      to grow on every call, which forced the disc to stop for each flip to
//      avoid drawing a wedge that fills; both halves of that are gone.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  LETTERS_MIN_CONSONANTS,
  LETTERS_MIN_VOWELS,
  LETTERS_TILE_COUNT,
  allowedPicks,
} from '@countdown/shared';
import type { ClientRound, LetterKind, LetterTile as LetterTileData } from '@countdown/shared';
import {
  ConnectionStatus,
  LetterTile,
  LiveClock,
  PlayerChip,
  RoundTypeChip,
  StandingsStrip,
  Button,
} from '../../components/primitives/index.js';
import { useClock } from '../../hooks/useClock.js';
import { useKeyboardMode } from '../../hooks/useKeyboardMode.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { isEditable } from '../../lib/isEditable.js';
import { playerById } from '../../lib/playerLookup.js';
import { useGameStore } from '../../store/gameStore.js';
import { prefersReducedMotion } from '../../store/settingsStore.js';
import type { RoomScreenProps } from './types.js';

/** Where each slot of the selection row sat, and how much of it was real. */
export interface BoardHandoff {
  /** The round it was measured on. A different round makes it stale. */
  round: number;
  /** When, so a rect from a screen the player has since left is not reused. */
  at: number;
  /** How many slots held a called tile. The rest were still face-down. */
  revealed: number;
  /** Slot order, which is board order here: the row is never rearranged. */
  rects: { left: number; top: number; width: number; height: number }[];
}

let handoff: BoardHandoff | null = null;

/**
 * The selection row's geometry, for the play screen to animate out of.
 *
 * The ninth tile and the phase change arrive in one payload, so the two rows
 * never coexist and there is no shared-element capture to take at swap time.
 * This is measured as each tile lands instead — the row does not move after the
 * last one — and read back one screen later.
 *
 * Taking it clears it. A second read is a screen that arrived some other way,
 * a reload into `letters-play` most often, and it must not animate out of a
 * board that was never on this device.
 */
export function takeBoardHandoff(): BoardHandoff | null {
  const held = handoff;
  handoff = null;
  return held;
}

/**
 * The shortest gap between two tiles landing, and the only pacing left.
 *
 * This used to be TILE_FLIP_MS. A tile was held for a whole flip so that the
 * next one could not cut it off mid-rotation, which fixed the flips and broke
 * the calling: a caller tapping faster than the beat pressed a button and
 * watched nothing happen for the better part of half a second. Flips overlap
 * now — every released tile owns its animation for TILE_FLIP_MS and nothing
 * takes it away — so the only thing left to enforce is that two tiles do not
 * land in the same frame, which is what makes the row read as one call at a
 * time rather than a board appearing.
 *
 * TILE_FLIP_MS is untouched as the length of a flip. It is no longer how long a
 * tile waits before it starts, and it is no longer a slice of the round clock
 * either — see the note below.
 */
const TILE_STAGGER_MS = 110;

// The disc used to stand still for the length of every flip, and there was a
// `useTileFlipHold` here that made it. It was drawing a real thing: the engine
// handed the caller `TILE_FLIP_MS` back per tile, `extendClock` did that by
// moving the deadline out, so `remaining / duration` stepped UP on every press
// — and a wedge that grows is the one thing `PieClock` forbids. Standing the
// disc still was the only honest way to draw it.
//
// Isaac watched it and read the nine stops as the timer breaking, which is
// fair: from the chair, a clock that stops nine times in a thirty-second round
// looks broken whatever it is compensating for. The engine no longer extends
// anything, so there is nothing left to draw and the disc simply falls. The
// caller now pays for the board being dealt, about four seconds of the thirty.
//
// `TILE_FLIP_MS` still exists and `motion.test.ts` still pins it to the
// stylesheet. It is the length of a flip. It is no longer a slice of anybody's
// budget.

/**
 * How many tiles the row has released.
 *
 * `revealed` only ever climbs toward `count`, one tile per stagger. Whatever is
 * already on the board at mount is simply there: a reload mid-selection is not
 * nine calls, and flipping them all in would be a lie about what just happened.
 *
 * **There was a `settled` beside it and it is deliberately gone.** It tracked
 * which tiles had finished flipping, purely so the row could hand a tile from
 * `revealing` to `available` at the end of its own animation — and Isaac
 * reported the result as the tile's top and bottom shading blinking off and
 * back on at the end of every flip, which is exactly what that swap does. The
 * two states paint identically by design, so the swap changed nothing anybody
 * was meant to see; what it changed was the DOM, tearing down the flip's
 * rotator and with it the compositing layer that `transform-style: preserve-3d`
 * and `backface-visibility` had promoted. The inset relief is re-rasterised
 * into the parent layer on the next frame, and that repaint is the blink.
 *
 * So a tile is decided ONCE and never changes state again: the ones that were
 * already on the board are `available` from their first frame and never flip,
 * and the ones that arrive while we are watching are `revealing` and stay that
 * way. The animation runs once and holds its end frame (`forwards`); nothing
 * about the tile changes until the phase does, and a phase change is a whole
 * new screen where a repaint costs nothing. `alreadyThere` is what keeps the
 * no-flip-on-reload behaviour that `settled` used to provide as a side effect.
 * See the note on `.tile-flip-in` in `index.css`, which lost its `will-change`
 * for the same reason.
 */
function usePacedReveal(count: number): { revealed: number; alreadyThere: number } {
  const [shown, setShown] = useState(count);
  // How many tiles were on the board before this screen started watching. They
  // are drawn as resting tiles and never flip: a reload mid-selection is not
  // nine calls, and flipping them all in would be a lie about what just
  // happened. It moves only when a board RESETS under us, which is the next
  // round starting without this component unmounting.
  const [alreadyThere, setAlreadyThere] = useState(count);
  const lastAt = useRef(0);

  useEffect(() => {
    if (shown === count) return;

    // Two cases have nothing to pace: a new board that is shorter than the old
    // one, and motion being off. Both still go through a timer rather than
    // setting state in the body of the effect, which cascades a render — and
    // the count the row actually draws is clamped below anyway, so the catch-up
    // is bookkeeping and never a frame the player sees.
    if (shown > count || prefersReducedMotion()) {
      const jump = window.setTimeout(() => {
        setShown(count);
        setAlreadyThere(count);
      }, 0);
      return () => window.clearTimeout(jump);
    }

    const wait = Math.max(0, lastAt.current + TILE_STAGGER_MS - Date.now());
    const timer = window.setTimeout(() => {
      lastAt.current = Date.now();
      setShown((current) => current + 1);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [shown, count]);

  return { revealed: Math.min(shown, count), alreadyThere: Math.min(alreadyThere, count) };
}

/**
 * Picks that have been sent and not yet answered.
 *
 * The press has to be acknowledged on the frame it happens, and the tile is a
 * network round trip away, so those cannot be the same acknowledgement. This is
 * the first one: it lights the slot the call is for and says nothing about what
 * will be in it. The server remains the only thing that decides what the letter
 * is, and whether the pick counted at all.
 *
 * It clears itself as answers land, and gives up on an answer that is not
 * coming — a pick the server refuses comes back as nothing at all, and a slot
 * cannot sit lit for the rest of the round waiting for it.
 */
function usePendingPicks(landed: number): { pending: number; sent: () => void } {
  const [pending, setPending] = useState(0);
  const seen = useRef(landed);

  useEffect(() => {
    if (landed === seen.current) return;
    const answered = Math.max(1, landed - seen.current);
    seen.current = landed;
    setPending((current) => Math.max(0, current - answered));
  }, [landed]);

  useEffect(() => {
    if (pending === 0) return;
    const giveUp = window.setTimeout(() => setPending(0), 2_000);
    return () => window.clearTimeout(giveUp);
  }, [pending]);

  return { pending, sent: useCallback(() => setPending((current) => current + 1), []) };
}

function countKind(tiles: LetterTileData[], kind: LetterKind): number {
  return tiles.filter((tile) => tile.kind === kind).length;
}

/**
 * Why a call is dead, on the only occasions one is.
 *
 * `canPick` refuses a kind exactly when the other kind's outstanding minimum
 * needs every slot that is left, so this line and a disabled button appear
 * together and it says the one thing the board does not already show. The rest
 * of what this used to narrate — how many tiles were left, which minimums were
 * outstanding, that both had been met, that nine tiles were up — is counted off
 * in front of the player by the row and the two bars.
 */
function footerLine(tiles: LetterTileData[]): string {
  const left = LETTERS_TILE_COUNT - tiles.length;
  if (left <= 0) return '';

  const needVowels = Math.max(0, LETTERS_MIN_VOWELS - countKind(tiles, 'vowel'));
  const needConsonants = Math.max(0, LETTERS_MIN_CONSONANTS - countKind(tiles, 'consonant'));

  if (needVowels === left) {
    return left === 1 ? 'the last one has to be a vowel' : 'the rest have to be vowels';
  }
  if (needConsonants === left) {
    return left === 1 ? 'the last one has to be a consonant' : 'the rest have to be consonants';
  }
  return '';
}

/**
 * The keyboard key that presses this button, as a badge beside the label.
 *
 * It inherits the button's own colour rather than picking one, so it stays
 * legible on the filled surface and on the flat disabled surface, in both
 * themes, without a second colour decision. Hidden from screen readers: the
 * button is already named, and `Vowel V` is not its name.
 */
function KeyBadge({ label }: { label: string }): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="grid h-6 w-6 place-items-center rounded-chip border border-current font-mono text-[12px] font-semibold opacity-70"
    >
      {label}
    </span>
  );
}

function MinimumBar({ called, minimum }: { called: number; minimum: number }): ReactElement {
  const met = called >= minimum;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between px-1 font-mono text-[13px] font-medium text-ink-muted">
        <span className="tabular">{called} called</span>
        <span>minimum {minimum}</span>
      </div>
      <div className="flex gap-[5px]" aria-hidden="true">
        {Array.from({ length: minimum }, (_, index) => (
          <div
            key={index}
            className={`h-[6px] flex-1 rounded-full transition-colors duration-300 ease-out ${
              index < Math.min(called, minimum)
                ? met
                  ? 'bg-good'
                  : 'bg-blue-400'
                : 'bg-line'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// A `BagPanel` was here: how many vowels and how many consonants were left in
// the piles, in two cards with their own meters, for the players who were not
// calling. Nobody plays the pile — the pick is a vowel or a consonant either
// way, and the counts changed nothing anybody could do — so it was a second
// screenful of furniture between the watcher and the board they were watching.

export function LettersSelectionScreen({ state, send }: RoomScreenProps): ReactElement {
  const round: ClientRound | undefined = state.rounds[state.currentRoundIndex];
  const quality = useGameStore((store) => store.quality);
  const isDesktop = useIsDesktop();
  const keyboard = useKeyboardMode();
  const reading = useClock(round?.clock ?? null, { frozen: state.serverPaused });

  const tiles: LetterTileData[] = round?.tiles ?? [];

  const chooser = playerById(state, round?.chooserId ?? null);
  const youAreCalling = chooser !== null && chooser.id === state.youId;
  const allowed = allowedPicks(tiles);

  // What the row shows, which trails what the server has sent by a stagger.
  // Everything else on the screen — the minimums, the buttons — runs off
  // `tiles`, because a legality question must never be answered by an
  // animation.
  const { revealed, alreadyThere } = usePacedReveal(tiles.length);
  const { pending, sent } = usePendingPicks(tiles.length);
  // Slots that have been called and are not on the board yet: the ones the row
  // is still working through, plus the ones still crossing the network.
  const called = Math.min(LETTERS_TILE_COUNT, tiles.length + pending);
  const roundIndex = round?.index;
  const boardRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const row = boardRef.current;
    if (row === null || roundIndex === undefined) return;
    handoff = {
      round: roundIndex,
      at: Date.now(),
      revealed,
      rects: Array.from(row.children).map((child) => {
        const rect = child.getBoundingClientRect();
        return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
      }),
    };
  }, [revealed, roundIndex]);

  // The call is acknowledged here, before it is sent, because the press and the
  // tile are two different events and the tile is a round trip away.
  const pick = useCallback(
    (kind: LetterKind) => {
      sent();
      send({ type: 'pick-letter', kind, actionId: crypto.randomUUID() });
    },
    [send, sent],
  );

  // V and C call a tile on a physical keyboard, which is the same budget the
  // thumbs are spending. Only the caller is listening, and only for a pick the
  // server would accept: a key that fires a refused action is worse than a key
  // that does nothing.
  const canVowel = youAreCalling && allowed.includes('vowel');
  const canConsonant = youAreCalling && allowed.includes('consonant');
  useEffect(() => {
    if (!canVowel && !canConsonant) return;
    if (keyboard.mode !== 'answer') return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      // **A called tile cannot be taken back**, and this is the only one-shot
      // spend in the app hanging off a window listener, so it gets the guard
      // even though nothing has gone wrong yet.
      //
      // `keyboard.mode` covers the chat drawer and covers it well, but it is a
      // per-instance flag that a field has to opt into — it is the room's own
      // bookkeeping rather than a fact about the event — so any text field that
      // does not flip it takes the letter AND calls a tile at the same time.
      // `isEditable` is the fact about the event, and it is what the play
      // screens have always used. It is deliberately NOT the wider
      // `isKeyHandlingControl`: V and C are not activation keys, so a focused
      // button will not also consume them, and standing aside for one would put
      // the hotkeys out of reach the moment somebody clicked `Vowel` — which is
      // exactly the round where they want to press V again.
      if (isEditable(event.target)) return;
      if (event.defaultPrevented) return;
      const key = event.key.toLowerCase();
      if (key === 'v' && canVowel) {
        event.preventDefault();
        pick('vowel');
      }
      if (key === 'c' && canConsonant) {
        event.preventDefault();
        pick('consonant');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canVowel, canConsonant, keyboard.mode, pick]);

  // Unreachable through RoomPage, which routes here by phase. It exists because
  // the phase router's screens must always return an element.
  if (round === undefined) {
    return <p className="p-6 text-ink-dim">Waiting for the round.</p>;
  }

  const vowels = countKind(tiles, 'vowel');
  const consonants = countKind(tiles, 'consonant');
  const footer = footerLine(tiles);

  const clockSize = isDesktop ? 200 : 128;
  const tileSize = isDesktop ? 'selection' : 'phone';

  return (
    <div className="flex min-h-full flex-col gap-5 px-4 py-5 min-[900px]:px-7">
      <header className="flex items-center gap-2">
        <RoundTypeChip type={round.type} size="sm" />
        <div className="min-w-0 flex-1 overflow-hidden">
          <StandingsStrip state={state} showRoundCounter />
        </div>
        <ConnectionStatus quality={quality} compact />
      </header>

      <div className="flex flex-col items-center gap-4 min-[900px]:gap-8">
        <LiveClock
          clock={round?.clock ?? null}
          frozen={state.serverPaused}
          size={clockSize}
          variant={reading.frozen ? 'frozen' : reading.urgent ? 'alert' : 'normal'}
          label={`${reading.seconds} seconds left`}
        />

        <div className="flex items-center gap-2">
          {youAreCalling ? (
            <>
              <span className="label-mono">you are calling</span>
              {chooser !== null && <PlayerChip player={chooser} size="md" />}
            </>
          ) : (
            <>
              {chooser !== null && <PlayerChip player={chooser} size="md" />}
              <span className="text-[15px] text-ink-muted">
                {chooser === null ? 'nobody is calling' : 'is calling'}
              </span>
            </>
          )}
        </div>

        <div
          ref={boardRef}
          data-board
          data-testid="tile-row"
          className="flex items-end gap-[5px] min-[900px]:gap-[9px]"
        >
          {Array.from({ length: LETTERS_TILE_COUNT }, (_, index) => {
            const tile = index < revealed ? tiles[index] : undefined;
            if (tile === undefined) {
              // An empty slot that has been called says so from the frame the
              // call was made, whatever the network and the row are still doing
              // with it. It never says what is coming: that is the server's.
              return (
                <LetterTile
                  key={index}
                  state="face-down"
                  size={tileSize}
                  className={index < called ? 'tile-called' : undefined}
                />
              );
            }
            // Every tile released in the last flip is still arriving, not just
            // the newest one. A fast caller overlaps flips rather than cutting
            // the one before off at whatever angle it had reached.
            return (
              <LetterTile
                key={index}
                letter={tile.letter}
                state={index < alreadyThere ? 'available' : 'revealing'}
                size={tileSize}
              />
            );
          })}
        </div>
      </div>

      {/* A player who is not calling gets the row filling in and nothing else.
          What was here — the pile counts, a second list of who is in the room,
          and a sentence about when their own clock would start — was three
          panels of reading between a watcher and the board they were watching,
          and the standings above already name everybody. */}
      {youAreCalling && (
        <div className="flex flex-col gap-5 min-[900px]:flex-row min-[900px]:justify-center min-[900px]:gap-11">
          <div className="flex flex-col gap-3 min-[900px]:w-[300px]">
            <Button
              variant="primary"
              size="xl"
              block
              className="call-button"
              disabled={!allowed.includes('vowel')}
              onClick={() => pick('vowel')}
            >
              Vowel
              {isDesktop && <KeyBadge label="V" />}
            </Button>
            <MinimumBar called={vowels} minimum={LETTERS_MIN_VOWELS} />
          </div>

          <div className="flex flex-col gap-3 min-[900px]:w-[300px]">
            <Button
              variant="primary"
              size="xl"
              block
              className="call-button"
              disabled={!allowed.includes('consonant')}
              onClick={() => pick('consonant')}
            >
              Consonant
              {isDesktop && <KeyBadge label="C" />}
            </Button>
            <MinimumBar called={consonants} minimum={LETTERS_MIN_CONSONANTS} />
          </div>
        </div>
      )}

      {/* A line of room whether or not there is a line, so a call falling to
          one kind does not shift the page under the next press. It is the
          caller's own dead button being explained, so nobody else sees it. */}
      {youAreCalling && (
        <p className="min-h-6 text-center font-mono text-sm font-medium text-ink-dim">{footer}</p>
      )}
    </div>
  );
}
